// Client for this app's own backend (session, sign-in, server-configured
// accounts, exchange rate). DigitalOcean API calls live in csvUtils.

const DEFAULT_TIMEOUT_MS = 15000;

export class ApiError extends Error {
  constructor(message, { status = 0, body = null, kind = 'http' } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.kind = kind; // 'http' | 'network' | 'timeout'
  }
}

// Empty in production, where the backend serves the app from the same origin.
export const getApiBase = () => process.env.REACT_APP_TOTP_API_URL || '';

// Sends the request and turns transport failures into ApiError.
const send = async (path, { method, body, timeoutMs }) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  // File uploads go as multipart form data; the browser sets that header itself
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  try {
    return await fetch(`${getApiBase()}${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : undefined,
      body: body === undefined || isForm ? body : JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    });
  } catch (err) {
    if (err && err.name === 'AbortError') {
      throw new ApiError('The server took too long to respond. Please try again.', { kind: 'timeout' });
    }
    throw new ApiError('Unable to reach the dashboard server. Check your connection and try again.', { kind: 'network' });
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const apiRequest = async (path, { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  const response = await send(path, { method, body, timeoutMs });

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }

  if (!response.ok) {
    const message = (data && data.error) || `The server responded with an error (${response.status}).`;
    throw new ApiError(message, { status: response.status, body: data });
  }
  return data;
};

// Fetches a file. Errors still arrive as JSON and are raised as ApiError.
export const apiDownload = async (path, { timeoutMs = 120000 } = {}) => {
  const response = await send(path, { method: 'GET', timeoutMs });

  if (!response.ok) {
    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      data = null;
    }
    const message = (data && data.error) || `The server responded with an error (${response.status}).`;
    throw new ApiError(message, { status: response.status, body: data });
  }
  return response.blob();
};
